/**
 * Tenant-aware Firestore operations
 * All queries are automatically scoped to the current club
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where
} from 'firebase/firestore';
import { db } from './firebase';

/**
 * Get all documents from a collection, filtered by clubId
 */
export async function getTenantDocs(collectionName, clubId, additionalQueries = []) {
  if (!clubId) throw new Error('clubId is required');

  const constraints = [where('clubId', '==', clubId), ...additionalQueries];
  const q = query(collection(db, collectionName), ...constraints);
  const snapshot = await getDocs(q);

  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));
}

/**
 * Get a single document (verifies it belongs to the club)
 */
export async function getTenantDoc(collectionName, docId, clubId) {
  if (!clubId) throw new Error('clubId is required');

  const docRef = doc(db, collectionName, docId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) return null;

  const data = docSnap.data();
  // Verify club ownership
  if (data.clubId !== clubId) {
    console.warn(`Access denied: Document ${docId} does not belong to club ${clubId}`);
    return null;
  }

  return { id: docSnap.id, ...data };
}

/**
 * Add a new document with clubId automatically set
 */
export async function addTenantDoc(collectionName, data, clubId, userId = null) {
  if (!clubId) throw new Error('clubId is required');

  const docData = {
    ...data,
    clubId,
    createdAt: new Date(),
    createdBy: userId
  };

  const docRef = await addDoc(collection(db, collectionName), docData);
  return { id: docRef.id, ...docData };
}

/**
 * Set a document with specific ID (includes clubId)
 */
export async function setTenantDoc(collectionName, docId, data, clubId, userId = null, merge = true) {
  if (!clubId) throw new Error('clubId is required');

  const docData = {
    ...data,
    clubId,
    updatedAt: new Date(),
    updatedBy: userId
  };

  await setDoc(doc(db, collectionName, docId), docData, { merge });
  return { id: docId, ...docData };
}

/**
 * Update a document (verifies club ownership first)
 */
export async function updateTenantDoc(collectionName, docId, data, clubId, userId = null) {
  if (!clubId) throw new Error('clubId is required');

  // Verify ownership first
  const existing = await getTenantDoc(collectionName, docId, clubId);
  if (!existing) {
    throw new Error('Document not found or access denied');
  }

  const updateData = {
    ...data,
    updatedAt: new Date(),
    updatedBy: userId
  };

  await updateDoc(doc(db, collectionName, docId), updateData);
  return { id: docId, ...existing, ...updateData };
}

/**
 * Delete a document (verifies club ownership first)
 */
export async function deleteTenantDoc(collectionName, docId, clubId) {
  if (!clubId) throw new Error('clubId is required');

  // Verify ownership first
  const existing = await getTenantDoc(collectionName, docId, clubId);
  if (!existing) {
    throw new Error('Document not found or access denied');
  }

  await deleteDoc(doc(db, collectionName, docId));
  return true;
}

/**
 * Get club-specific content (homepage, clubInfo, etc.)
 */
export async function getClubContent(contentType, clubId) {
  if (!clubId) throw new Error('clubId is required');

  const docRef = doc(db, 'content', `${clubId}_${contentType}`);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) return null;
  return { id: docSnap.id, ...docSnap.data() };
}

/**
 * Set club-specific content
 */
export async function setClubContent(contentType, data, clubId, userId = null) {
  if (!clubId) throw new Error('clubId is required');

  const docId = `${clubId}_${contentType}`;
  const docData = {
    ...data,
    clubId,
    updatedAt: new Date(),
    updatedBy: userId
  };

  await setDoc(doc(db, 'content', docId), docData, { merge: true });
  return { id: docId, ...docData };
}

/**
 * Collections that need tenant scoping
 */
export const TENANT_COLLECTIONS = [
  'players',
  'teams',
  'events',
  'products',
  'orders',
  'registrations',
  'announcements'
];

/**
 * Check if a collection needs tenant scoping
 */
export function needsTenantScope(collectionName) {
  return TENANT_COLLECTIONS.includes(collectionName);
}
